import { useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import NoShowAppointmentCard from "../../features/no-show-appointments/NoShowAppointmentCard";
import NoShowAppointmentResponseCard from "../../features/no-show-appointments/NoShowAppointmentResponseCard";
import { getNoShowReasons } from "../../utility/functions/common-functions";
import { useAppSelector } from "../../store/hooks";
import { useGetNoShowAppointmentsQuery } from "../../features/no-show-appointments/services/GetNoShowAppointmentsApiSlice";
import { useSaveNoShowAppointmentFeedbackMutation } from "../../features/no-show-appointments/services/SaveNoShowAppointmentFeedbackApiSlice";
import { useSaveNoShowAppointmentSkippedMutation } from "../../features/no-show-appointments/services/SaveNoShowAppointmentSkippedApiSlice";
import CustomWidgets from "./CustomWidgets";
import { useGetReasonMessagesQuery } from "../opt-in-opt-out/services/GetReasonMessageAPISlice";
import { SelectOption } from "../../utility/interfaces/onboardingDataInterfaces";
import { Box } from "@mui/material";
import styles from "./PatientDashboard.module.scss";
import CelebrationModalContainer from "../../features/banner-overlay/CelebrationModalContainer";

// -------------------- Component --------------------
const PatientDashboard = () => {
  const { t } = useTranslation();

  const hideNoShow = useAppSelector(
    (state) => state.patientOnboardingStatus.hideNoShow
  );

  // -------------------- Redux Selectors --------------------
  const patientId = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.patientProfileId
  );
  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );
  const patientCaseId = useAppSelector(
    (state) => state.patientOnboardingStatus.patientCaseId
  );

  // -------------------- API Hooks --------------------
  const { data: reasonList } = useGetReasonMessagesQuery(patientPreferredLang, {
    skip: !patientPreferredLang,
  });

  const { data: noShowAppointmentsData, isLoading,  refetch } =
    useGetNoShowAppointmentsQuery(
      {
        patientProfileId: patientId,
        patientCaseId,
        preferredLanguage: patientPreferredLang,
      },
      { skip: hideNoShow }
    );

  const [saveNoShowAppointmentFeedback] =
    useSaveNoShowAppointmentFeedbackMutation();
  const [saveNoShowAppointmentSkipped] =
    useSaveNoShowAppointmentSkippedMutation();

  // -------------------- State --------------------

  const [noShowModalOpen, setNoShowModalOpen] = useState(false);
  const [noShowResponsecardModalOpen, setNoShowResponseCardModalOpen] =
    useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");

  // -------------------- Derived Data --------------------
  const noShowReasons: SelectOption[] = useMemo(() => {
    return getNoShowReasons(reasonList?.categoryReasons || []).map(
      ({ displayReason, reasonId }) => ({
        label: displayReason,
        value: reasonId,
      })
    );
  }, [reasonList?.categoryReasons]);

  // -------------------- Effects --------------------
  useEffect(() => {
    const hasNoShowAppointments =
      noShowAppointmentsData?.patientNoShowAppointments?.length > 0;

    // Open the modal only once on initial load, not after user closes it
    if (
      hasNoShowAppointments &&
      !noShowModalOpen &&
      !noShowResponsecardModalOpen
    ) {
      setNoShowModalOpen(true);
    }
  }, [noShowAppointmentsData, noShowModalOpen, noShowResponsecardModalOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // -------------------- Handlers --------------------

  const handleCloseModal = useCallback(() => setNoShowModalOpen(false), []);
  const handleCloseNoShowResponseCardModal = useCallback(
    () => setNoShowResponseCardModalOpen(false),
    []
  );

  const handleConfirmNoShow = useCallback(
    async (reasonId: number) => {
      try {
        const csnId = noShowAppointmentsData.patientNoShowAppointments[0].csnid;

        await saveNoShowAppointmentFeedback({
          body: {
            patientProfileId: patientId,
            patientAppointmentCsnid: csnId,
            reasonId,
            comments: "",
          },
        });

        await refetch();

        setThankYouMessage(
          noShowAppointmentsData.patientNoShowAppointments[0]
            .noShowThankYouMessage || ""
        );
        setNoShowModalOpen(false);
        setNoShowResponseCardModalOpen(true);
      } catch (error) {
        enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
        setNoShowModalOpen(false);
      }
    },
    [patientId, saveNoShowAppointmentFeedback, noShowAppointmentsData, t]
  );

  const handleSkipNoShow = useCallback(async () => {
    try {
      const csnId = noShowAppointmentsData.patientNoShowAppointments[0].csnid;
      await saveNoShowAppointmentSkipped({
        body: {
          patientProfileId: patientId,
          patientAppointmentCsnid: csnId,
        },
      });

      await refetch();

      setNoShowModalOpen(false);
      setNoShowResponseCardModalOpen(false);
    } catch (error) {
      enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
      setNoShowModalOpen(false);
    }
  }, [patientId, saveNoShowAppointmentSkipped, noShowAppointmentsData, t]);

  // -------------------- Render --------------------
  return (
    <Box className={styles.patientDashboard}>
      {/* Content with higher z-index */}
      <Box className={styles.contentWrapper}>
        <CustomWidgets />

        {!isLoading &&
          !hideNoShow &&
          noShowModalOpen &&
          noShowAppointmentsData?.patientNoShowAppointments?.length > 0 && (
            <NoShowAppointmentCard
              modalOpen={noShowModalOpen}
              onCloseModal={handleCloseModal}
              handleConfirmNoShow={handleConfirmNoShow}
              handleSkipNoShow={handleSkipNoShow}
              noShowReasons={noShowReasons}
              patientNoShowMessage={
                noShowAppointmentsData.patientNoShowAppointments[0]
                  .patientNoShowMessage
              }
            />
          )}

        {!noShowModalOpen && !isLoading && !noShowResponsecardModalOpen && (
          <CelebrationModalContainer />
        )}

        {noShowResponsecardModalOpen && (
          <NoShowAppointmentResponseCard
            modalOpen={noShowResponsecardModalOpen}
            onCloseModal={handleCloseNoShowResponseCardModal}
            patientNoShowMessage={thankYouMessage}
          />
        )}
      </Box>
    </Box>
  );
};

export default PatientDashboard;
